import { print } from "graphql";
import gql from "graphql-tag";
import { useQuery } from "react-query";
import { getFreshAccessToken } from "../use-user";

// Query to lookup takeback by shipping tracking ID (TODO: remove this)
const LOOKUP_BY_TRACKING = gql`
  query LookupByTracking($shipping_tracking_id: String!) {
    takebacks(filter: { shipping_tracking_id: { eq: $shipping_tracking_id } }) {
      items {
        created_at
        customer_id
        data
        deleted_at
        eventId
        id
        product_id
        quantity
        schema_version
        shipping_provider_id
        shipping_tracking_id
        source
        takeback_processing_history_list {
          parent_status
          processed_at
          id
          status
          updated_at
        }
        timestamp
        type_name
        updated_at
        customer_id_belongsTo {
          name
          last_name
          id
        }
        shippingss {
          items {
            tracking_number
          }
        }
      }
    }
  }
`;

const LOOKUP_BY_SHIPPING_TRACKING = gql`
  query GetShippingLabelTracking($id: String!) {
    shippingses(filter: { tracking_number: { eq: $id } }) {
      items {
        tracking_number
        takeback_id
      }
    }
  }
`;

// Query to lookup takeback by ID
const LOOKUP_BY_ID = gql`
  query LookupById($id: String!) {
    takeback(id: $id) {
      created_at
      customer_id
      data
      deleted_at
      eventId
      id
      product_id
      quantity
      schema_version
      shipping_provider_id
      shipping_tracking_id
      source
      takeback_processing_history_list {
        parent_status
        processed_at
        id
        status
        updated_at
      }
      timestamp
      type_name
      updated_at
      customer_id_belongsTo {
        name
        last_name
        id
      }
      shippingss {
        items {
          tracking_number
        }
      }
    }
  }
`;

// Cache for in-flight requests to prevent race conditions
const inflightRequests = new Map();

export const useTakebackLookup = () => {
  const performGraphQLLookup = async ({ query, variables }) => {
    const accessToken = getFreshAccessToken();
    const res = await fetch(`${process.env.REACT_APP_TES_URL}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "x-client-id": process.env.REACT_APP_CLIENT_ID || "lego-test",
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({
        query: print(query),
        variables,
      }),
    });

    if (!res.ok) {
      throw new Error(
        `GraphQL request failed: ${res.status} ${res.statusText}`
      );
    }

    const result = await res.json();

    if (result.errors) {
      throw new Error(
        `GraphQL errors: ${result.errors.map((e) => e.message).join(", ")}`
      );
    }

    return result;
  };

  // Single function that handles the lookup logic
  const lookupTakeback = async (identifier) => {
    if (!identifier?.trim()) {
      return null;
    }

    const trimmedIdentifier = identifier.trim();

    // Check if we already have an in-flight request for this identifier
    if (inflightRequests.has(trimmedIdentifier)) {
      return inflightRequests.get(trimmedIdentifier);
    }

    // Create the lookup promise
    const lookupPromise = (async () => {
      try {
        // Try looking up by tracking ID first (TODO: remove this)
        try {
          const trackingResult = await performGraphQLLookup({
            query: LOOKUP_BY_TRACKING,
            variables: { shipping_tracking_id: trimmedIdentifier },
          });
          const trackingData = trackingResult?.data?.takebacks?.items?.[0];

          if (trackingData) {
            return trackingData;
          }
        } catch (error) {
          console.warn("Tracking lookup failed:", error);
          // Continue to shipping lookup
        }

        // Try looking up by shipping tracking ID
        let takebackId = trimmedIdentifier;
        try {
          const shippingsResult = await performGraphQLLookup({
            query: LOOKUP_BY_SHIPPING_TRACKING,
            variables: { id: trimmedIdentifier },
          });
          const shippingsData = shippingsResult?.data?.shippingses?.items?.[0];

          if (shippingsData?.takeback_id) {
            takebackId = shippingsData.takeback_id;
          }
        } catch (error) {
          console.warn("Shipping lookup failed:", error);
          // Continue with original identifier
        }

        // Finally, try looking up by takeback ID
        try {
          const idResult = await performGraphQLLookup({
            query: LOOKUP_BY_ID,
            variables: { id: takebackId },
          });
          const idData = idResult?.data?.takeback;

          if (idData) {
            return idData;
          }
        } catch (error) {
          console.warn("ID lookup failed:", error);
        }

        // No results found
        return null;
      } finally {
        // Clean up the in-flight request
        inflightRequests.delete(trimmedIdentifier);
      }
    })();

    // Store the promise in the cache
    inflightRequests.set(trimmedIdentifier, lookupPromise);

    return lookupPromise;
  };

  return lookupTakeback;
};

// Hook for reactive takeback lookup with React Query caching
export const useTakebackLookupQuery = (identifier, options = {}) => {
  const lookupTakeback = useTakebackLookup();

  return useQuery(
    ["takeback-lookup", identifier],
    () => lookupTakeback(identifier),
    {
      enabled: Boolean(identifier?.trim()),
      retry: 2,
      refetchOnWindowFocus: false,
      ...options,
    }
  );
};
